'use strict'

import AbstractService from '@/app/service/abstract-service'
import ApiBaseResponse from '@/app/service/api/api-base-response'
import ApiResponseCode from '@/app/service/api/api-response-code'
import Clients from '@/app/service/api/model/clients'
import Cookies from 'js-cookie'
import * as types from '@/store/types'
import { Client } from '@stomp/stompjs'
import Helper from '@/utils/helper'
import CommonConfig from '@/app/config/common-config'
import { eventBus } from '@/app/event-bus.js'
import AppInfoHelper from '@/utils/app-info-helper'
import Face from '@/app/service/api/model/face'
import { colorSchemes, settings } from '@/app/const/settings'
import AdPlaylists from '@/app/service/api/model/ad/ad-playlists'

const root = document.documentElement
const RaifSizeLandscape = 9
const RaifSizePortrait = 7

class ApiService extends AbstractService {
  constructor (http, store, pageManager) {
    super()
    this.$http = http
    this.$store = store
    this.$pageManager = pageManager
    this.stompClient = null
    this.action = null
  }

  login (login, password) {
    return new Promise((resolve, reject) => {
      this.$http({
        method: 'put',
        url: 'auth/login',
        data: {
          login,
          password
        }
      }).then(({ data }) => {
        resolve({
          uuid: data.uuid,
          idBranch: data.idBranch,
          id: login
        })
      })
        .catch((e) => {
          const response = new ApiBaseResponse(e.response.data)
          reject(response)
        })
        .catch((e) => {
          reject(new ApiBaseResponse({
            code: ApiResponseCode.UNKNOWN.value
          }))
        })
    })
  }

  logout () {
    return new Promise((resolve, reject) => {
      const { uuid } = JSON.parse(Cookies.get('userData'))
      this.$http({
        method: 'put',
        url: 'auth/logout',
        headers: {
          'X-Paylogic-Queue-Token-Header': uuid
        }
      }).then(() => {
        resolve()
      }).catch((e) => {
        const response = new ApiBaseResponse(e.response.data)
        reject(response)
      })
        .catch((e) => {
          reject(new ApiBaseResponse({
            code: ApiResponseCode.UNKNOWN.value
          }))
        })
    })
  }

  onMessageState (info) {
    if (process.env.VUE_APP_CONSOLE === 'ON') {
      console.log(info)
    }

    if (Helper.isDefined(info.headers['X-Softlogic-Version'])) {
      AppInfoHelper.updateVersion(info.headers['X-Softlogic-Version'])
    }

    const { type, data } = JSON.parse(info.body)
    if (type === 'state') {
      let clients
      try {
        clients = new Clients(data)
      } catch (e) {
        // do noting
      }
      eventBus.emitUpdateClients(clients)
    }
  }

  subscribeUpdateState () {
    const { uuid, id } = JSON.parse(Cookies.get('userData'))
    const headers = { 'X-Paylogic-Queue-Token-Header': uuid }

    const config = {
      brokerURL: CommonConfig.getWsApi(),
      debug: () => {},
      connectHeaders: headers,
      reconnectDelay: 5000
    }
    if (process.env.VUE_APP_CONSOLE === 'ON') {
      config.debug = function (str) {
        console.log(str)
      }
    }

    // Конфигурация сокета
    this.stompClient = new Client(config)

    this.stompClient.onConnect = () => {
      this.$store.dispatch(types.NETWORK.socket.update.connected, { connected: true })
      this.stompClient.subscribe('/screen/' + id + '/**', this.onMessageState, headers)
    }

    // Обработка ошибок от сокета
    this.stompClient.onStompError = (frame) => {
      this.$store.dispatch(types.NETWORK.socket.update.connected, { connected: false })
    }
    this.stompClient.onDisconnect = (frame) => {
      this.$store.dispatch(types.NETWORK.socket.update.connected, { connected: false })
    }
    this.stompClient.onWebSocketClose = (frame) => {
      this.$store.dispatch(types.NETWORK.socket.update.connected, { connected: false })
    }
    // Активация сокета
    this.stompClient.activate()
  }

  disconnect () {
    if (Helper.isDefined(this.stompClient)) {
      this.stompClient.deactivate()
    }
    this.$store.dispatch(types.NETWORK.socket.update.connected, { connected: null })
  }

  getSetting () {
    return new Promise((resolve, reject) => {
      const { uuid } = JSON.parse(Cookies.get('userData'))
      this.$http({
        method: 'get',
        url: 'settings/face',
        headers: {
          'X-Paylogic-Queue-Token-Header': uuid
        }
      })
        .then(({ data }) => {
        const face = new Face(data)
        resolve(face)
      })
        .catch((e) => {
        let response
        try {
          response = new ApiBaseResponse(e.response.data)
        } catch (e) {
          response = new ApiBaseResponse({
            code: ApiResponseCode.UNKNOWN.value
          })
        }
        reject(response)
      })
    })
  }

  updateGuiSetting (face) {
    this.$store.dispatch(types.GUI.settings.set, face)

    // Настройки цветовых схем
    const { colors } = settings.colorScheme.find(item => item.code === face.getColorScheme().getCode())

    if (Helper.isDefined(colors)) {
      colors.forEach((item) => {
        root.style.setProperty(item.name, item.value)
      })
    }

    // Настройки для шаблона колонок
    const { sizeLandscape } = settings.layout.find((el) => el.code === face.getLayout().getCode())
    const { sizePortrait } = settings.layout.find((el) => el.code === face.getLayout().getCode())
    let size = null

    // Для темы raiffeisen устанавливаем кастомное количество колонок
    if (face.getColorScheme().getCode() === colorSchemes.raif) {
      size = window.innerHeight <= window.innerWidth ? RaifSizeLandscape : RaifSizePortrait
    } else {
      size = window.innerHeight <= window.innerWidth ? sizeLandscape : sizePortrait
    }

    if (Helper.isDefined(size)) {
      this.$store.dispatch(types.BOARD.size.set, size)
    }

    if (Helper.isDefined(face.getScreenSetting())) {
      // Установка настроек плеера
      const viewport = document.getElementById('viewport')
      viewport.content = `width=${face.getScreenSetting().getWidth()},initial-scale=${face.getScreenSetting().getScale()},shrink-to-fit=${face.getScreenSetting().getShrinkToFit()}`

      // Установить состояния шапки
      this.$store.dispatch(types.GUI.header.set, face.getScreenSetting().getHideHeader())
    }

    const hasHideCursor = !face.getSoftwareSettings() || face.getSoftwareSettings().hasHideCursor()
    document.body.style.cursor = hasHideCursor ? 'none' : 'default'

    // Настройка состояний
    const states = face.getStates()

    if (!Helper.isEmpty(states)) {
      this.$store.dispatch(types.BOARD.states.set, states)
    }

    // Настройка громкости табло
    if (Helper.isDefined(face.getVolumeSetting())) {
      this.$store.dispatch(types.PLAYLIST.volumeSetting.set, face.getVolumeSetting())
    }
  }

  getState () {
    return new Promise((resolve, reject) => {
      const { uuid } = JSON.parse(Cookies.get('userData'))
      this.$http({
        method: 'get',
        url: 'clients/state',
        headers: {
          'X-Paylogic-Queue-Token-Header': uuid
        }
      }).then(({ data }) => {
        const clients = new Clients(data)
        resolve(clients)
      }).catch((e) => {
        let response
        try {
          response = new ApiBaseResponse(e.response.data)
        } catch (e) {
          response = new ApiBaseResponse({
            code: ApiResponseCode.UNKNOWN.value
          })
        }
        reject(response)
      })
    })
  }

  getAdv () {
    return new Promise((resolve, reject) => {
      const { uuid } = JSON.parse(Cookies.get('userData'))
      this.$http({
        method: 'get',
        url: 'ad/playlists',
        headers: {
          'X-Paylogic-Queue-Token-Header': uuid
        }
      }).then(({ data }) => {
        const playlists = new AdPlaylists(data)
        resolve(playlists)
      }).catch((e) => {
        let response
        try {
          response = new ApiBaseResponse(e.response.data)
        } catch (e) {
          response = new ApiBaseResponse({
            code: ApiResponseCode.UNKNOWN.value
          })
        }
        reject(response)
      })
    })
  }
}

export default ApiService
